import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import authService from '../../../api-authorization/AuthorizeService';

interface IAddSprayProfileDialogProps {
  clientId: string;
  tractorId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  name: string;
  totalBoomWidthFt: number;
  maxSprayWidthIn: number;
  broadcastAppRateGpm: number;
  isDefault: boolean;
};

const AddSprayProfileDialog: React.FC<IAddSprayProfileDialogProps> = ({
  clientId,
  tractorId,
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const { control, handleSubmit, register, watch } = useForm<FormValues>();

  const addSprayProfile = async (data: FormValues) => {
    setLoading(true);
    const token = await authService.getAccessToken();
    const body = [
      {
        name: data.name,
        totalBoomWidthFt: data.totalBoomWidthFt,
        maxSprayWidthIn: data.maxSprayWidthIn,
        broadcastAppRateGpm: data.broadcastAppRateGpm,
        isDefault: data.isDefault,
        clientId: clientId,
        tractorId: tractorId
      }
    ];

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/spray-profiles`,
      {
        method: 'POST',
        headers: !token
          ? {}
          : {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
        body: JSON.stringify(body)
      }
    );
    if (response.ok) {
      toast.success('Spray profile added successfully!');
      onSuccess();
    } else {
      toast.error(`${response.status} ${response.statusText}`);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <AddIcon />
        Add Spray Profile
      </DialogTitle>
      <form onSubmit={handleSubmit(addSprayProfile)}>
        <DialogContent sx={{ maxWidth: '480px', paddingTop: 0 }}>
          <Typography gutterBottom>
            Add a new spray profile to the sprayer.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    type="text"
                    disabled={loading}
                    InputProps={{ inputProps: { step: 'any' } }}
                    {...field}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Broadcast App Rate (gpm)</FormLabel>
              <Controller
                control={control}
                name="broadcastAppRateGpm"
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    type="number"
                    disabled={loading}
                    InputProps={{ inputProps: { step: 'any' } }}
                    {...field}
                  />
                )}
              />
            </FormControl>
            <Stack direction="row" spacing={2}>
              <FormControl>
                <FormLabel>Total Boom Width Ft</FormLabel>
                <Controller
                  control={control}
                  name="totalBoomWidthFt"
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      disabled={loading}
                      InputProps={{ inputProps: { step: 'any' } }}
                      {...field}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Max Spray Width (in)</FormLabel>
                <Controller
                  control={control}
                  name="maxSprayWidthIn"
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      disabled={loading}
                      InputProps={{ inputProps: { step: 'any' } }}
                      {...field}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outlined" disabled={loading} type="submit">
            Add Spray Profile
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddSprayProfileDialog;
